import React, {
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { connect } from "react-redux";

import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faCamera } from "@fortawesome/pro-solid-svg-icons/faCamera";
import { faLasso } from "@fortawesome/pro-solid-svg-icons/faLasso";
import { faMap } from "@fortawesome/pro-solid-svg-icons/faMap";
import { faMapPin } from "@fortawesome/pro-solid-svg-icons/faMapPin";
import { faSearchDollar } from "@fortawesome/pro-solid-svg-icons/faSearchDollar";
import { useIntl } from "react-intl";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { FindMeButton, Interactive, ToolButton, ZoomButtons } from "@app/component/map/FeaturedMap";
import { selectMapTool, toggleLeadFinder } from "@app/scene/map/store/actions";
import { getMapViewTool, isConfigurationVisible } from "@app/scene/map/store/selectors";
import useMapSnapshotTaker from "@app/scene/map/utils/useMapSnapshotTaker";
import { getLeadGenPlan, isCurrentUserOwner, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import BaseMapStyleModal from "./BaseMapStyleModal";
import GoToLocationToolButton from "./GoToLocationToolButton";
import messages from "./messages";
import styles from "./ToolSelectionPanel.module.scss";

export const toolIcon: Record<MapTool, IconDefinition> = {
  [MapTool.DROP_PIN]: faMapPin,
  [MapTool.FIND_LEADS]: faSearchDollar,
  [MapTool.LASSO]: faLasso,
  [MapTool.PIN_LOCATION]: faMapPin,
  [MapTool.TAKE_SNAPSHOT]: faCamera,
};

interface Props {
  availableTools?: MapTool[];
  configurationVisible: boolean;
  isOwner: boolean;
  isReadOnlyMember: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  selectedTool?: MapTool;
  selectMapTool: typeof selectMapTool;
  toggleLeadFinder: typeof toggleLeadFinder;
}

const defaultAvailableTools = [
  MapTool.DROP_PIN,
  MapTool.FIND_LEADS,
  MapTool.LASSO,
  MapTool.TAKE_SNAPSHOT,
];

const ToolSelectionPanel: React.FC<Props> = ({
  availableTools = defaultAvailableTools,
  configurationVisible,
  isOwner,
  isReadOnlyMember,
  leadGenPlan,
  selectedTool,
  selectMapTool,
  toggleLeadFinder,
}) => {
  const analytics = useExtendedAnalytics("Map Controls", useAnalytics());
  const intl = useIntl();

  const baseMapRef = useRef<HTMLDivElement>(null);

  const takeMapSnapshot = useMapSnapshotTaker();

  const handleToolClick = useCallback(
    (clickedTool: MapTool) => {
      if (clickedTool === MapTool.TAKE_SNAPSHOT) {
        analytics.clicked(["Take Snapshot"]);
        takeMapSnapshot();
      } else if (clickedTool === MapTool.FIND_LEADS) {
        analytics.clicked(["Find Leads"]);
        if (leadGenPlan === LeadFinderSubscriptionPlan.NONE) {
          showLeadFinderUpsell(leadGenPlan, intl, isOwner);
        } else {
          toggleLeadFinder();
        }
      } else {
        selectMapTool(selectedTool === clickedTool ? undefined : { mapTool: clickedTool });
        if (clickedTool === MapTool.LASSO) {
          analytics.clicked(["Lasso"]);
        } else if (clickedTool === MapTool.DROP_PIN) {
          analytics.clicked(["Drop Pin"]);
        }
      }
    },
    [
      analytics,
      takeMapSnapshot,
      leadGenPlan,
      intl,
      isOwner,
      toggleLeadFinder,
      selectMapTool,
      selectedTool,
    ]
  );

  const [baseMapModalVisible, , hideBaseMapSelection, toggleBaseMapSelection] = useBoolean();

  const handleMouseDown = useCallback(
    (event?: KeyboardEvent | MouseEvent | SyntheticEvent<HTMLElement>) => {
      const toolButton = (event?.target as HTMLElement).closest(".mmc-tool-button");
      if (toolButton) {
        // We prevent mouse down to prevent closing opened popovers, e.g. lead finder popover
        // Because if it was open and then closed because of this mouse down event, then
        // the click button event would open it again. Whereas we wanna toggle the popover
        // on click.
        event?.preventDefault();
        event?.stopPropagation();
      }
    },
    []
  );

  const filteredTools = useMemo(
    () =>
      availableTools.filter(
        (tool) => !([MapTool.DROP_PIN, MapTool.FIND_LEADS].includes(tool) && isReadOnlyMember)
      ),
    [availableTools, isReadOnlyMember]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <div className={styles.container}>
        {filteredTools.length > 0 && (
          <div className={styles.mapTools} onMouseDown={handleMouseDown}>
            {filteredTools.map((tool) => (
              <Interactive className={styles.wrapper} key={tool}>
                <ToolButton
                  active={
                    (configurationVisible && tool === MapTool.FIND_LEADS) || tool === selectedTool
                  }
                  className="mmc-tool-button"
                  icon={toolIcon[tool]}
                  onClick={() => handleToolClick(tool)}
                  title={intl.formatMessage(messages[tool])}
                />
              </Interactive>
            ))}
          </div>
        )}

        <Interactive className={styles.mapControls}>
          <div className={styles.mapTools}>
            <GoToLocationToolButton />
            <FindMeButton />
          </div>

          <div className={styles.zoomControls}>
            <ZoomButtons />
          </div>

          <div className={styles.singleControl}>
            <ToolButton
              active={baseMapModalVisible}
              icon={faMap}
              onClick={toggleBaseMapSelection}
              ref={baseMapRef}
              title={intl.formatMessage(messages.selectBasemap)}
            />
          </div>

          <BaseMapStyleModal
            baseMapButtonRef={baseMapRef.current}
            onHide={hideBaseMapSelection}
            visible={baseMapModalVisible}
          />
        </Interactive>
      </div>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  configurationVisible: isConfigurationVisible(state),
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  leadGenPlan: getLeadGenPlan(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  selectMapTool,
  toggleLeadFinder,
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolSelectionPanel);
